import React, { useEffect, useRef } from 'react';
import { useDispatch } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import classnames from 'classnames';

import { useShortSelector } from 'react-redux-app/lib/modules/core/hooks';
import { useCurrentLang } from 'react-redux-app/lib/modules/i18n/hooks';

import { l } from 'react-redux-app/lib/modules/i18n/utils';
import { isDesktop } from '../../../core/utils/dom';
import {
  getHowToStartUrl,
  getRevenueUrl,
  getCampaignsStatsUrl,
  getAffiliatedUsersUrl,
  getAffiliatedWebmastersUrl,
  getBalanceAdjustmentsUrl,
  getPayoutRequestsUrl,
  getPayoutFeesUrl,
  getPayoutSettingsUrl,
  getCampaignsUrl,
  getAffiliateToolsUrl,
  getPostbackSettingsUrl,
  getSitesUrl,
  getSiteAliasesUrl,
  getBannersUrl,
} from '../../utils/siteUrls';

import { getIsLeftMenuOpened } from '../../../ui/selectors';

import { actionSwitchLeftMenu } from '../../../ui/actions';

import { ActionLink } from 'react-redux-app/lib/modules/actionLink/components';
import { Heading } from 'react-redux-app/lib/modules/heading/components';
import BigButton from '../../../core/components/buttons/BigButton';
import LangLink from '../../../core/i18n/components/LangLink';

import styles from './styles/index.css.json';


export default () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const isLeftMenuOpened = useShortSelector(getIsLeftMenuOpened);
  const lang = useCurrentLang();

  const closeTimeOut = useRef();
  const savedIsDesktop = useRef(isDesktop());

  const isFirstRenderCompleted = useRef(false);

  const onResize = () => {
    if (savedIsDesktop.current !== isDesktop()) {
      savedIsDesktop.current = isDesktop();
      dispatch(actionSwitchLeftMenu(savedIsDesktop.current));
    }
  };

  useEffect(() => {
    isFirstRenderCompleted.current = true;
    dispatch(actionSwitchLeftMenu(savedIsDesktop.current));

    window.addEventListener('resize', onResize);

    return () => { window.removeEventListener('resize', onResize); };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const closeNoneDesktopMenuOnClick = () => {
    if (!savedIsDesktop.current) {
      if (closeTimeOut.current) {
        clearTimeout(closeTimeOut.current);
      }

      closeTimeOut.current = setTimeout(() => {
        if (isFirstRenderCompleted.current) {
          dispatch(actionSwitchLeftMenu(false));
        }
      }, 200);
    }
  };

  const createCampaignLinkProps = {
    className: classnames(styles.leftMenu__link, styles.leftMenu__item),
    onClick: closeNoneDesktopMenuOnClick,
  };

  const linkProps = {
    ...createCampaignLinkProps,
    activeClassName: styles.leftMenu__activeLink,
  };

  return (
    <div
      className={classnames(
        styles.leftMenu,
        {
          [styles.leftMenu_opened]:
            isLeftMenuOpened || (!isFirstRenderCompleted.current && savedIsDesktop.current),
        }
      )}
    >
      <ActionLink
        className={styles.leftMenu__close}
        iconBefore={styles.icon_close}
        onClick={() => { dispatch(actionSwitchLeftMenu()); }}
      />

      <BigButton
        className={classnames(styles.button, styles.leftMenu__howToStart)}
        onClick={() => {
          navigate(getHowToStartUrl(lang));
          closeNoneDesktopMenuOnClick();
        }}
      >
        {l('action.how-to-start')}
      </BigButton>

      <div className={styles.leftMenu__links}>
        <Heading theme={{ heading: styles.leftMenu__groupHeader }} level={2}>
          {l('header.stats')}
        </Heading>
        <LangLink {...linkProps} to={getCampaignsStatsUrl()}>
          {l('menu.statistics-overview')}
        </LangLink>
        <LangLink {...linkProps} to={getRevenueUrl()}>
          {l('menu.revenue')}
        </LangLink>
        <LangLink {...linkProps} to={getAffiliatedUsersUrl()}>
          {l('menu.users')}
        </LangLink>
        <LangLink {...linkProps} to={getAffiliatedWebmastersUrl()}>
          {l('menu.webmasters')}
        </LangLink>
        <LangLink {...linkProps} to={getBalanceAdjustmentsUrl()}>
          {l('menu.balance-adjustments')}
        </LangLink>

        <Heading theme={{ heading: styles.leftMenu__groupHeader }} level={2}>
          {l('header.payouts')}
        </Heading>
        <LangLink {...linkProps} to={getPayoutSettingsUrl()}>
          {l('menu.request-payout')}
        </LangLink>
        <LangLink {...linkProps} to={getPayoutRequestsUrl()}>
          {l('menu.payout-history')}
        </LangLink>
        <LangLink {...linkProps} to={getPayoutFeesUrl()}>
          {l('menu.payout-fee')}
        </LangLink>

        <Heading theme={{ heading: styles.leftMenu__groupHeader }} level={2}>
          {l('header.campaigns')}
        </Heading>
        <LangLink {...createCampaignLinkProps} to={getCampaignsUrl(null, true)}>
          {l('menu.create-campaign')}
        </LangLink>
        <LangLink {...linkProps} to={getCampaignsUrl()}>
          {l('menu.campaign-list')}
        </LangLink>
        <LangLink {...linkProps} to={getAffiliateToolsUrl()}>
          {l('menu.affiliate-tools')}
        </LangLink>
        <LangLink {...linkProps} to={getPostbackSettingsUrl()}>
          {l('menu.postback-settings')}
        </LangLink>

        <Heading theme={{ heading: styles.leftMenu__groupHeader }} level={2}>
          {l('header.white-label-sites')}
        </Heading>
        <LangLink {...linkProps} to={getSitesUrl()}>
          {l('menu.my-sites')}
        </LangLink>
        <LangLink {...linkProps} to={getSiteAliasesUrl()}>
          {l('menu.site-aliases')}
        </LangLink>
        <LangLink {...linkProps} to={getBannersUrl()}>
          {l('menu.banners')}
        </LangLink>
      </div>
    </div>
  );
};
